import React, { useEffect, useState } from 'react';
import { GridCellParams, GridColumnOrderChangeParams, GridColumnResizeParams, useGridApiRef } from "@mui/x-data-grid-pro";
import { OrderList, useGetOrdersQuery, useGetOrdersAllQuery } from "../../services/grace";
import OrderGrid from "../../components/grids/OrderGrid/OrderGrid";
import { Box, Toolbar } from "@mui/material";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from '../../context/AlertContext/AlertContext';
import GDGAppBar from "../../components/app/GDGAppBar/GDGAppBar";
import GDGAppBarButton from "../../components/app/GDGAppBar/GDGAppBarButton";
import { GridColumnDef } from "../../components/grids/grid";
import { setColumnDef } from "../../features/order/orderSlice";
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { RootState } from "../../util/store";

enum PageModes {
    List = 'list',
    All = 'all',
}

declare type PageMode = PageModes.List | PageModes.All;

export default function OrderStatus(): JSX.Element {
    const apiRef = useGridApiRef();
    const dispatch = useAppDispatch();
    const alertDispatch = useAlertDispatch();

    const columnDef = useAppSelector((state: RootState) => state.order.columnDef);

    const [pageMode, setPageMode] = useState<PageMode>(PageModes.List);

    const {
        data: basicData,
        refetch: basicRefetch,
        error: basicError,
        isError: basicIsError,
        isFetching: basicIsFetching,
    } = useGetOrdersQuery(null, { skip: pageMode!=PageModes.List });

    const {
        data: advancedData,
        refetch: advancedRefetch,
        error: advancedError,
        isError: advancedIsError,
        isFetching: advancedIsFetching
    } = useGetOrdersAllQuery(null, { skip: pageMode!=PageModes.All });


    const [orderRows, setOrderRows] = useState<OrderList>([]);

    useEffect(() => {
        if (basicIsFetching) {
            setOrderRows([]);
        } else if (basicIsError) {
            const err = basicError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to load order data",
                    }
                });
            }
        } else if (basicData !== undefined) {
            setOrderRows(basicData);
        }
    }, [basicData, basicIsFetching, basicIsError, basicError, alertDispatch]);

    useEffect(() => {
        if (advancedIsFetching) {
            setOrderRows([]);
        } else if (advancedIsError) {
            const err = basicError as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Error retrieving inventory data - please try again",
                    }
                });
            }
        } else if (advancedData !== undefined) {
            setOrderRows(advancedData);
        }
    }, [advancedData, advancedIsFetching, advancedIsError, advancedError, alertDispatch]);

    const handleOrdersReload = () => {
        setPageMode(PageModes.List);
        basicRefetch();
    };

    const handleOrdersLoadAll = () => {
        setPageMode(PageModes.All);
        advancedRefetch();
    };

    const handleCellClick = (params: GridCellParams, event: React.MouseEvent) => {
        if (params.colDef.field === 'orderDetails') {
            event.stopPropagation();
            if (params.id !== undefined) {
                window.open(`/orderdetails/${params.id}`)
            }
        }
    };

    const handleColumnResize = (params: GridColumnResizeParams) => {
        const newColumnDef:GridColumnDef[] = [];
        columnDef.forEach((item) => {
            if (item.field === params.colDef.field) {
                newColumnDef.push({...item, width: params.width});
            } else {
                newColumnDef.push({...item});
            }
        });
        dispatch(setColumnDef(newColumnDef));
    };

    const handleColumnReorder = (params: GridColumnOrderChangeParams) => {
        const newColumnDef = [...columnDef];
        const item = newColumnDef.splice(params.oldIndex, 1);
        newColumnDef.splice(params.targetIndex, 0, item[0])
        dispatch(setColumnDef(newColumnDef));
    };

    return (
        <Box sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <GDGAppBar title={'Orders'}>
                <GDGAppBarButton
                    text={'Reload'}
                    onClick={handleOrdersReload}
                />
                <GDGAppBarButton
                    text={'Load All'}
                    onClick={handleOrdersLoadAll}
                />
            </GDGAppBar>

            <OrderGrid
                apiRef={apiRef}
                loading={basicIsFetching || advancedIsFetching}
                rows={orderRows}
                columnDef={columnDef}
                onCellClick={handleCellClick}
                onColumnResize={handleColumnResize}
                onColumnOrderChange={handleColumnReorder}
            />
        </Box>
    );
}
